<template>
  <div class="section-layout">
    <aside class="rail" :aria-label="`Rubriques : ${title}`">
      <h3 class="rail-title">{{ title }}</h3>
      <nav class="rail-items">
        <router-link v-for="item in items" :key="item.to" :to="item.to" class="it" :class="{on: isOn(item), 'separator-after': item.separatorAfter}" active-class="" exact-active-class=""
                     :aria-current="isOn(item) ? 'page' : undefined">
          <span class="ic">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path :d="iconPath(item.icon)"/></svg>
          </span>
          <b>{{ item.label }}</b>
          <span v-if="item.badges && item.badges.some(badge => badge.count > 0)" class="badges">
            <template v-for="badge in item.badges" :key="badge.tone">
              <span v-if="badge.count > 0" class="badge" :class="badge.tone" :title="badge.title" :aria-label="badge.title">{{ badge.count > 99 ? '99+' : badge.count }}</span>
            </template>
          </span>
        </router-link>
      </nav>
      <!-- Réglages qui valent pour toutes les rubriques du menu : dans le même bloc, juste sous le menu -->
      <section v-if="$slots.controls" class="shared" aria-label="Réglages communs à toutes les rubriques">
        <div class="shared-head">
          <span class="shared-title">Réglages communs</span>
          <span class="shared-sub">&uarr; S'appliquent à toutes les rubriques ci-dessus</span>
        </div>
        <slot name="controls"/>
      </section>
    </aside>
    <div class="section-content">
      <slot/>
    </div>
  </div>
</template>

<script>
import ICONS from "@/components/sideMenuIcons";

/**
 * Page de rubrique : menu latéral (une entrée par sous-page) et contenu à droite.
 * items : [{to, label, icon, active?, badges?, separatorAfter?}] (separatorAfter : trait de séparation sous l'entrée) ; active est déduit de l'adresse si absent.
 * badges : [{count, tone: 'info' | 'warn' | 'danger', title}] affiche une pastille de compteur par élément (masquée à 0) avec une infobulle.
 * Slot « controls » : réglages communs à toutes les sous-pages (période, découpage…), affichés dans le même bloc que le menu.
 */
export default {
  name: 'SideMenuLayout',
  props: {
    title: {type: String, required: true},
    items: {type: Array, required: true},
  },
  methods: {
    iconPath(icon) {
      return ICONS[icon] ?? ICONS.list;
    },
    isOn(item) {
      if (item.active !== undefined) return item.active;
      const path = this.$route.path;
      return path === item.to || path.startsWith(item.to + '/');
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl'

.section-layout
  display flex
  align-items flex-start
  gap 20px
  width 100%
  padding-top 24px
  box-sizing border-box

  @media (max-width: DESKTOP_SIZE)
    flex-direction column
    align-items stretch

.rail
  width 268px
  flex none
  box-sizing border-box
  display flex
  flex-direction column
  gap 4px
  padding 14px
  border 1px solid PAGE_RULE
  border-radius 16px
  background white
  box-shadow 0 1px 0 rgba(23, 35, 63, .04), 0 12px 28px -20px rgba(23, 35, 63, .4)

  @media (max-width: DESKTOP_SIZE)
    width auto

.rail-title
  margin 2px 8px 8px
  font-size 13px
  font-weight 700
  letter-spacing .12em
  text-transform uppercase
  color CTRL_TEXT_MUTED

.rail-items
  display flex
  flex-direction column
  gap 4px

.it
  position relative
  display flex
  align-items center
  gap 12px
  padding 9px 10px
  border-radius 12px
  color FONT_COLOR
  text-decoration none
  transition background-color .15s ease

  b
    font-size 15px

  &:hover
    background-color PAGE_BG

.ic
  display grid
  place-items center
  width 38px
  height 38px
  flex none
  border-radius 10px
  background-color CTRL_BG
  color THEME_COLOR_PRIMARY
  transition background-color .15s ease

// Compteurs : bleu = information, orange = à traiter, rouge = argent dû
.badges
  display inline-flex
  gap 4px
  margin-left auto

.badge
  min-width 24px
  padding 2px 8px
  border-radius 999px
  box-sizing border-box
  text-align center
  font-size 12px
  font-weight 800
  line-height 1.5
  background #E4EEFC
  color #2F5FA8

  &.warn
    background #FFF0DA
    color #C25E00

  &.danger
    background #FFEDE8
    color #C2381F

// Séparation entre une entrée de synthèse (ex. « Tous ») et les rubriques détaillées
.it.separator-after
  margin-bottom 9px
  &::after
    content ''
    position absolute
    left 10px
    right 10px
    bottom -7px
    height 1px
    background-color PAGE_RULE

.it.on
  background-color PAGE_LEMON_HOVER
  box-shadow inset 4px 0 0 PAGE_LEMON

  .ic
    background-color PAGE_LEMON

// Bloc des réglages communs, collé au menu : fond citron pâle et liseré citron, comme l'entrée active
.shared
  display flex
  flex-direction column
  gap 12px
  margin-top 10px
  padding 14px
  border-radius 12px
  background-color #FFFBE0
  border 1px solid #F3E58C

.shared-head
  display flex
  flex-direction column
  gap 2px

.shared-title
  font-size 13px
  font-weight 700
  letter-spacing .08em
  text-transform uppercase
  color THEME_COLOR_PRIMARY

.shared-sub
  font-size 12px
  color CTRL_TEXT_MUTED

.section-content
  flex 1
  min-width 0
</style>
